import { FunctionComponent, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './mergeTwoChildsDialog.module.scss'
import { useTranslation } from 'react-i18next'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import SelectUnique from '@/components/_custom/Selector/selectUnique/selectUnique'
import { OptionsGroupDeprecated, SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import { GET_PARENT_LIST, MERGE_TWO_CHILDS } from '@/modules/API/parent/parentHandler'
import { Parent } from '@/types/Parent'
import { Child, ChildDaycare } from '@/types/Child'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import Loading from '@/components/Loading/Loading'
import { Merge } from '@mui/icons-material'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface MergeTwoChildsDialogProps {
    open: boolean
    handleClose: () => void
    childId: number
    childName: string
    daycareId: number
    handleRefetchChild: () => void
}

const MergeTwoChildsDialog: FunctionComponent<MergeTwoChildsDialogProps> = ({
    open = false,
    handleClose,
    childId,
    daycareId,
    childName,
    handleRefetchChild
}: MergeTwoChildsDialogProps) => {
    const { t } = useTranslation()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [parentList, setParentList] = useState<Parent[]>([])
    const [selectedOldChildId, setSelectedOldChildId] = useState<string>('0')

    const {
        loading: loadingGetParents,
        data: dataGetParents,
        error: errorGetParents
    } = useQuery(GET_PARENT_LIST, {
        variables: {
            daycare_id: daycareId
        }
    })

    useEffect(() => {
        if (dataGetParents) {
            setParentList(dataGetParents.getParentList)
        }
    }, [dataGetParents])

    useEffect(() => {
        if (errorGetParents) {
            handleClose()
        }
    }, [errorGetParents])

    const [mergeTwoChilds] = useMutation(MERGE_TWO_CHILDS, {
        variables: {
            oldChildId: Number(selectedOldChildId),
            daycareId: daycareId,
            newChildId: childId
        },
        onCompleted: () => {
            handleRefetchChild()

            showNotificationSuccess(t('mergeSuccess'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('mergeError'))
            handleClose()
        }
    })

    const selectOptions: SelectOptionsDeprecated = parentList
        .map((parent: Parent): OptionsGroupDeprecated | undefined => {
            const parentChilds: Child[] = parent.childs.filter((child: Child): boolean => !(child.id === childId))
            if (parentChilds.length > 0) {
                return {
                    name: parent.firstname + ' ' + parent.lastname,
                    options: parentChilds.map((child: Child): SelectOptionDeprecated => {
                        const complement: string | undefined = child.daycares.find(
                            (daycare: ChildDaycare) => daycare.daycareId === daycareId
                        )?.complement

                        return {
                            value: child.id.toString(),
                            name: child.firstname + (complement ? ` - ${complement}` : '')
                        }
                    })
                }
            }
        })
        .filter(
            (option: OptionsGroupDeprecated | undefined): boolean => option !== undefined
        ) as OptionsGroupDeprecated[]

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('mergeChild')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {t('whichChildYouWantToMerge')}
                    <span className={styles.TextFamily}>{childName}</span>
                    {' ?'}
                </p>
                <p className={styles.Text}>{t('noRollBack')}</p>
                {loadingGetParents && <Loading overall={false} />}
                {errorGetParents && <ErrorMessage error={t('noChild')} />}
                {dataGetParents && (
                    <>
                        {selectOptions.length > 0 ? (
                            <div className={styles.InputContainer}>
                                <SelectUnique
                                    selectOptions={selectOptions}
                                    selectValue={selectedOldChildId}
                                    setSelectValue={setSelectedOldChildId}
                                    className={styles.Select}
                                    label={t('childs')}
                                />
                            </div>
                        ) : (
                            <ErrorMessage className={styles.Text} error={t('noChild')} />
                        )}
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('merge')}
                                customIcon={<Merge />}
                                onClick={mergeTwoChilds}
                                className={styles.Button}
                                disabled={selectedOldChildId === '0'}
                            />
                            <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                        </div>
                    </>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default MergeTwoChildsDialog
